/**
 * Тренерский workspace PWA (P2, §7 COACH): экраны ПУЛЬС / ПРОГРАММЫ /
 * РАСПИСАНИЕ. Данные — только Server-Side Truth из `/api/v1` §6.5/§6.6
 * (I-7 паритет с TG-ответами); мутации — ТОЛЬКО через Confirm-Gate (§8):
 * первый вызов ничего не пишет и выдаёт карточку черновика (opcard.ts),
 * исполнение — кнопкой «Подтвердить» (серверный harness.dispatch, I-1).
 *
 * Правки событий/статусы (M2, данные) — вне гейта, статус-кво Phase 13.
 * Строки — только из словарей `t()`/`plural()`; даты/числа — только `Intl`.
 */

import {
  type ClientProgramRow,
  type CoachBusiness,
  type GatedResult,
  type OperationCard,
  type ScheduleData,
  type ScheduleEvent,
  ApiError,
  getCoachBusiness,
  getCoachClients,
  getCoachProgramDetail,
  getCoachPrograms,
  getCoachPulse,
  getCoachSchedule,
  patchCoachScheduleEvent,
  postAdvancePhase,
  postCoachSchedule,
  postProgramSave,
} from "./api";
import { fmtDate, fmtDateTime, fmtNumber, plural, t } from "./i18n";
import { renderOperationCard } from "./opcard";
import { qrSvg } from "./qr";

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
}

function shell(className: string, html: string): HTMLElement {
  const div = document.createElement("section");
  div.className = className;
  div.innerHTML = html;
  return div;
}

function failBlock(): HTMLElement {
  return shell("screen", `<p class="muted">${t("error.generic")}</p>`);
}

/**
 * Монтирует карточку подтверждения из ответа gated-мутации (§8);
 * если это не гейт — показывает ошибку/статус текстом.
 */
export function mountGatedResult(
  status: HTMLElement,
  res: GatedResult,
  onDone?: (ok: boolean) => void,
): void {
  status.textContent = "";
  const op = res.operation as OperationCard | null | undefined;
  if (res.error === "confirmation_required" && op) {
    renderOperationCard(status, op, onDone);
    return;
  }
  if (res.ok === true) {
    status.textContent = t("cabinet.saved");
    onDone?.(true);
    return;
  }
  status.textContent = res.error ? `${res.error}` : t("error.generic");
}

function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: { error?: string }; error?: string } | null;
    const code = d?.detail?.error ?? d?.error;
    if (code) return String(code);
  }
  return t("error.generic");
}

// ── ПУЛЬС (тренер) ───────────────────────────────────────────────────────────

export function renderPulseScreen(
  container: HTMLElement,
  onOpenBrief: (clientTg: number) => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let data;
    try {
      data = await getCoachPulse();
    } catch {
      container.appendChild(failBlock());
      return;
    }
    if (data.ok !== true) {
      container.appendChild(
        shell("screen", `<p class="muted">${esc(data.error ?? "")}</p>`),
      );
      return;
    }

    const totals = data.totals;
    // v2.1 (UX-07): Пульс = рабочий inbox. «Нужно внимание» — карточки с
    // контекстом (тишина/задания/бриф) и inline-действием; «Далее сегодня» —
    // события; остальные клиенты — компактный список.
    const attentionClients = data.clients.filter((c) => c.attention);
    const otherClients = data.clients.filter((c) => !c.attention);

    const attentionHtml = attentionClients.length
      ? `<h3 class="screen__subtitle">${t("pulse.attention_title")} · ${attentionClients.length}</h3>
         <ul class="list">${attentionClients
          .map((c) => {
            const ctx: string[] = [];
            if (c.silence_days != null) {
              ctx.push(plural("pulse.silence", c.silence_days));
            }
            if ((c.tasks_open ?? 0) > 0) {
              ctx.push(`${t("pulse.tasks_open")}: ${c.tasks_open}`);
            }
            return `<li class="list__row list__row--stack card card--inbox">
                 <span class="list__what"><b>${esc(c.name ?? String(c.client_tg_id))}</b></span>
                 ${ctx.length ? `<span class="muted">${esc(ctx.join(" · "))}</span>` : ""}
                 ${
                   c.brief
                     ? `<span class="muted">${esc(c.brief)}</span>`
                     : ""
                 }
                 <span class="row">
                   <button class="linkbtn js-open-brief" data-tg="${c.client_tg_id}">${t("pulse.open_brief")}</button>
                 </span>
               </li>`;
          })
          .join("")}</ul>`
      : "";

    const eventsHtml = data.events.length
      ? `<h3 class="screen__subtitle">${t("pulse.events")}</h3>
         <ul class="list">${data.events
          .map(
            (e) =>
              `<li class="list__row${
                e.client_tg_id ? " list__row--action" : ""
              }"${e.client_tg_id ? ` data-tg="${e.client_tg_id}"` : ""}>
                 <span class="list__when">${esc(e.hhmm)}</span>
                 <span class="list__what">${esc(e.client_name ?? t("pulse.external"))}${
                   e.activity ? ` <span class="muted">${esc(e.activity)}</span>` : ""
                 }${
                   e.is_nika
                     ? ` <span class="badge badge--nika">${t("pulse.nika_badge")}</span>`
                     : ""
                 }</span>
               </li>`,
          )
          .join("")}</ul>`
      : `<p class="muted">${t("pulse.no_events")}</p>`;

    const othersHtml = otherClients.length
      ? `<h3 class="screen__subtitle">${t("pulse.clients_title")}</h3>
         <ul class="list">${otherClients
          .map((c) => {
            const flags: string[] = [];
            if (c.silence_days != null) {
              flags.push(
                `<span class="muted">${plural("pulse.silence", c.silence_days)}</span>`,
              );
            }
            return `<li class="list__row list__row--action list__row--stack"
                        data-tg="${c.client_tg_id}">
               <span class="list__what">${esc(c.name ?? String(c.client_tg_id))}
                 ${flags.join(" ")}${
                   c.brief
                     ? `<br><span class="muted">${esc(c.brief)}</span>`
                     : ""
                 }</span>
             </li>`;
          })
          .join("")}</ul>`
      : `<p class="muted">${t("pulse.no_clients")}</p>`;

    const view = shell(
      "screen",
      `<h2 class="screen__title">${t("pulse.title")} — ${fmtDate(data.date)}</h2>
       <div class="card">
         <div class="kv"><span class="kv__k">${t("pulse.trainings")}</span>
           <span class="kv__v">${totals.trainings}</span></div>
         <div class="kv"><span class="kv__k">${t("pulse.nika_clients")}</span>
           <span class="kv__v">${totals.nika_clients}</span></div>
         <div class="kv"><span class="kv__k">${t("pulse.clients")}</span>
           <span class="kv__v">${totals.clients}</span></div>
         <div class="kv"><span class="kv__k">${t("pulse.attention")}</span>
           <span class="kv__v">${totals.attention}</span></div>
       </div>
       ${attentionHtml}
       ${eventsHtml}
       ${othersHtml}`,
    );
    view.querySelectorAll<HTMLElement>(".list__row--action").forEach((row) => {
      row.addEventListener("click", () =>
        onOpenBrief(Number(row.dataset.tg)));
    });
    view.querySelectorAll<HTMLButtonElement>(".js-open-brief").forEach((b) =>
      b.addEventListener("click", () => onOpenBrief(Number(b.dataset.tg))));
    container.appendChild(view);
  })();
}

// ── ПРОГРАММЫ (тренер) ───────────────────────────────────────────────────────

export function renderProgramsScreen(
  container: HTMLElement,
  onOpenBrief: (clientTg: number) => void,
  onEditWithNika?: (prefill: string) => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let data;
    try {
      data = await getCoachPrograms();
    } catch {
      container.appendChild(failBlock());
      return;
    }
    if (data.ok !== true) {
      container.appendChild(
        shell("screen", `<p class="muted">${esc(data.error ?? "")}</p>`),
      );
      return;
    }

    const mineHtml = data.authored.length
      ? `<ul class="list">${data.authored
          .map(
            (p) =>
              `<li class="list__row list__row--action" data-program="${p.program_id}">
                 <span class="list__what">${esc(p.title)}</span>
                 <span class="list__when">${t("programs.version", { v: p.version })}</span>
               </li>`,
          )
          .join("")}</ul>`
      : `<p class="muted">${t("programs.no_mine")}</p>`;

    const clientRowsHtml = data.client_programs.length
      ? data.client_programs.map((row) => clientProgramRow(row)).join("")
      : `<p class="muted">${t("programs.no_clients")}</p>`;

    const view = shell(
      "screen",
      `<h2 class="screen__title">${t("programs.title")}</h2>
       <h3 class="screen__subtitle">${t("programs.mine")}</h3>
       ${mineHtml}
       <h3 class="screen__subtitle">${t("programs.clients")}</h3>
       <div class="js-client-programs">${clientRowsHtml}</div>
       <h3 class="screen__subtitle">${t("programs.new_title")}</h3>
       <div class="card">
         <p class="muted">${t("programs.new_hint")}</p>
         <div class="form">
           <label class="form__label">${t("programs.field_title")}
             <input class="form__input js-prog-title" type="text"></label>
           <label class="form__label">${t("programs.field_slug")}
             <input class="form__input js-prog-slug" type="text"></label>
           <label class="form__label">${t("programs.field_payload")}
             <textarea class="form__textarea js-prog-payload" rows="6"></textarea></label>
           <button class="form__btn js-prog-save">${t("programs.save")}</button>
           <div class="form__status js-prog-status"></div>
         </div>
       </div>`,
    );
    container.appendChild(view);

    // Открытие версии программы (v1..vN) — чтение, ownership серверный.
    view.querySelectorAll<HTMLElement>("[data-program]").forEach((row) => {
      row.addEventListener("click", () =>
        renderProgramDetail(
          container,
          Number(row.dataset.program),
          () => renderProgramsScreen(container, onOpenBrief, onEditWithNika),
          onEditWithNika,
        ));
    });

    // Перевод фазы клиента — gated (kind=program_phase).
    bindAdvanceForms(view, onOpenBrief);

    // Сохранение программы/новой версии — gated (kind=program_save).
    const status = view.querySelector<HTMLElement>(".js-prog-status")!;
    view.querySelector<HTMLButtonElement>(".js-prog-save")!
      .addEventListener("click", () => {
        const title =
          view.querySelector<HTMLInputElement>(".js-prog-title")!.value.trim();
        const slug =
          view.querySelector<HTMLInputElement>(".js-prog-slug")!.value.trim();
        const raw =
          view.querySelector<HTMLTextAreaElement>(".js-prog-payload")!.value.trim();
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(raw || "{}") as Record<string, unknown>;
        } catch {
          status.textContent = t("programs.json_invalid");
          return;
        }
        if (!title) {
          status.textContent = t("error.generic");
          return;
        }
        status.textContent = t("cabinet.saved");
        void (async () => {
          try {
            const res = await postProgramSave({
              title,
              payload,
              ...(slug ? { slug } : {}),
            });
            mountGatedResult(status, res);
          } catch (err) {
            status.textContent = errText(err);
          }
        })();
      });
  })();
}

function clientProgramRow(row: ClientProgramRow): string {
  const phase = row.phase ? `${row.phase}` : "—";
  const day =
    row.day_in_phase != null
      ? ` · ${t("today.day", { n: row.day_in_phase })}`
      : "";
  return `<div class="card">
     <div class="card__title">${esc(row.client_name ?? String(row.client_tg_id))}</div>
     <div class="muted">${esc(row.program_title)} (${t("programs.version", { v: row.version })})</div>
     <div class="muted">${t("programs.phase")}: ${esc(phase)}${day}</div>
     <div class="form">
       <label class="form__label">${t("programs.advance_hint")}
         <input class="form__input js-adv-phase" type="text"></label>
       <button class="form__btn js-adv-run"
               data-assignment="${row.assignment_id}"
               data-tg="${row.client_tg_id}">${t("programs.advance")}</button>
       <div class="form__status js-adv-status"></div>
     </div>
   </div>`;
}

function bindAdvanceForms(root: HTMLElement, onOpenBrief: (clientTg: number) => void): void {
  root.querySelectorAll<HTMLButtonElement>(".js-adv-run").forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest(".card")!;
      const status = card.querySelector<HTMLElement>(".js-adv-status")!;
      const toPhase =
        card.querySelector<HTMLInputElement>(".js-adv-phase")!.value.trim() ||
        undefined;
      status.textContent = t("cabinet.saved");
      void (async () => {
        try {
          const res = await postAdvancePhase(
            Number(btn.dataset.assignment),
            toPhase,
          );
          mountGatedResult(status, res);
        } catch (err) {
          status.textContent = errText(err);
        }
      })();
    });
    // открыть бриф клиента по имени в карточке
    const title = btn.closest(".card")!.querySelector<HTMLElement>(".card__title");
    title?.addEventListener("click", () =>
      onOpenBrief(Number(btn.dataset.tg)));
  });
}

function renderProgramDetail(
  container: HTMLElement,
  programId: number,
  onBack: () => void,
  onEditWithNika?: (prefill: string) => void,
): void {
  container.innerHTML = "";
  void (async () => {
    let data;
    try {
      data = await getCoachProgramDetail(programId);
    } catch {
      container.appendChild(failBlock());
      return;
    }
    if (data.ok !== true) {
      container.appendChild(
        shell("screen", `<p class="muted">${esc(data.error ?? "")}</p>`),
      );
      return;
    }
    const p = data.program;
    const payload = (p.payload ?? {}) as Record<string, unknown>;
    const phases = Array.isArray(payload.phases) ? (payload.phases as unknown[]) : [];
    const principles =
      typeof payload.general_principles === "string"
        ? payload.general_principles
        : "";
    // v2.1 Phase 24 (UX-13, контракт PROGRAM-DESIGN-PAYLOAD-CONTRACT-v1 §7):
    // NIKA-generated маркер — наличие `generation`; rationale/stop_conditions —
    // OPTIONAL (старые payload рендерятся как раньше, UI ключи не требует).
    const isCoDesign = payload.generation != null;
    const rationale = Array.isArray(payload.rationale)
      ? (payload.rationale as Array<Record<string, unknown>>)
      : [];
    const stopConditions = Array.isArray(payload.stop_conditions)
      ? (payload.stop_conditions as string[])
      : [];
    const phasesHtml = phases.length
      ? `<h3 class="screen__subtitle">${t("programs.phases_title")}</h3>
         ${phases
          .map((ph, i) => {
            const phase = (ph ?? {}) as Record<string, unknown>;
            const title =
              typeof phase.title === "string" ? phase.title : `#${i + 1}`;
            const dur = (phase.duration ?? {}) as Record<string, unknown>;
            const days = typeof dur.days === "number" ? dur.days : null;
            const blocks = Array.isArray(phase.blocks)
              ? (phase.blocks as Array<Record<string, unknown>>)
              : [];
            const blocksHtml = blocks.length
              ? `<ul class="list">${blocks
                  .map((bl) => {
                    const b = (bl ?? {}) as Record<string, unknown>;
                    const btitle =
                      typeof b.title === "string" ? b.title : "";
                    const content =
                      typeof b.content === "string" ? b.content : "";
                    return `<li class="list__row list__row--stack">
                         <span class="list__what">${esc(btitle)}${
                           b.kind ? ` <span class="muted">${esc(String(b.kind))}</span>` : ""
                         }${
                           content
                             ? `<br><span class="muted">${esc(content.slice(0, 300))}</span>`
                             : ""
                         }</span>
                       </li>`;
                  })
                  .join("")}</ul>`
              : "";
            return `<div class="card">
                 <div class="card__title">${i + 1}. ${esc(title)}${
                   days !== null ? ` <span class="muted">~${days} ${t("roadmap.days")}</span>` : ""
                 }</div>
                 ${blocksHtml}
               </div>`;
          })
          .join("")}`
      : "";
    const rationaleHtml = rationale.length
      ? `<h3 class="screen__subtitle">${t("programs.why_title")}</h3>
         <ul class="list">${rationale
          .map((r) => {
            const decision = typeof r.decision === "string" ? r.decision : "";
            const risk = typeof r.risk_note === "string" ? r.risk_note : "";
            return `<li class="list__row list__row--stack">
                 <span class="list__what">${esc(decision)}${
                   risk ? `<br><span class="muted">${esc(risk)}</span>` : ""
                 }</span>
               </li>`;
          })
          .join("")}</ul>`
      : "";
    const risksHtml = stopConditions.length
      ? `<h3 class="screen__subtitle">${t("programs.risks_title")}</h3>
         <ul class="list">${stopConditions
          .map(
            (s) =>
              `<li class="list__row"><span class="list__what">${esc(String(s))}</span></li>`,
          )
          .join("")}</ul>`
      : "";
    const versionsHtml = data.versions
      .map(
        (v) =>
          `<li class="list__row">
             <span class="list__when">${t("programs.version", { v: v.version })}</span>
             <span class="list__what">${esc(v.title)}</span>
           </li>`,
      )
      .join("");

    container.appendChild(
      shell(
        "screen",
        `<button class="linkbtn js-detail-back">${t("programs.back")}</button>
         <h2 class="screen__title">${esc(p.title)}</h2>
         <div class="card">
           ${isCoDesign ? `<p><span class="badge badge--nika">${t("programs.co_created")}</span></p>` : ""}
           <div class="kv"><span class="kv__k">${t("programs.version", { v: p.version })}</span>
             <span class="kv__v">${p.is_active ? t("cabinet.on") : t("cabinet.off")}</span></div>
           <div class="kv"><span class="kv__k">${t("programs.phase")}</span>
             <span class="kv__v">${phases.length}</span></div>
           ${principles ? `<p class="muted">${esc(principles.slice(0, 300))}</p>` : ""}
         </div>
         ${phasesHtml}
         ${rationaleHtml}
         ${risksHtml}
         ${
           onEditWithNika
             ? `<button class="btn js-edit-nika">${t("programs.edit_with_nika")}</button>`
             : ""
         }
         <h3 class="screen__subtitle">${t("programs.detail_title")}</h3>
         <ul class="list">${versionsHtml}</ul>`,
      ),
    );
    container
      .querySelector(".js-detail-back")!
      .addEventListener("click", onBack);
    container
      .querySelector<HTMLButtonElement>(".js-edit-nika")
      ?.addEventListener("click", () =>
        onEditWithNika?.(`${t("programs.edit_prefill")} «${p.title}» (${t("programs.version", { v: p.version })}): `),
      );
  })();
}

// ── РАСПИСАНИЕ (тренер) ──────────────────────────────────────────────────────

const MINSK_OFFSET = "+03:00"; // Europe/Minsk без DST — фиксированный сдвиг

function statusLabels(): Record<string, string> {
  return {
    planned: t("schedule.status.planned"),
    completed: t("schedule.status.completed"),
    missed: t("schedule.status.missed"),
    rescheduled: t("schedule.status.rescheduled"),
    cancelled: t("schedule.status.cancelled"),
  };
}

export function renderScheduleScreen(container: HTMLElement): void {
  container.innerHTML = "";
  const view = shell(
    "screen",
    `<h2 class="screen__title">${t("schedule.title")}</h2>
     <div class="row js-periods">
       <button class="linkbtn js-period" data-period="today">${t("schedule.today")}</button>
       <button class="linkbtn js-period" data-period="tomorrow">${t("schedule.tomorrow")}</button>
       <button class="linkbtn js-period" data-period="week">${t("schedule.week")}</button>
     </div>
     <div class="js-schedule-body"><p class="muted">${t("cabinet.push_working")}</p></div>
     <h3 class="screen__subtitle">${t("schedule.new")}</h3>
     <div class="card">
       <div class="form">
         <label class="form__label">${t("schedule.field_client")}
           <input class="form__input js-ev-client" type="text"></label>
         <label class="form__label">${t("schedule.field_start")}
           <input class="form__input js-ev-start" type="datetime-local"></label>
         <label class="form__label">${t("schedule.field_activity")}
           <input class="form__input js-ev-activity" type="text"></label>
         <label class="form__label">${t("schedule.field_location")}
           <input class="form__input js-ev-location" type="text"></label>
         <button class="form__btn js-ev-add">${t("schedule.add")}</button>
         <div class="form__status js-ev-status"></div>
       </div>
     </div>`,
  );
  container.appendChild(view);

  // v2.1 (UX-10): пикер клиента — datalist поверх свободного ввода (прогрессивно:
  // свободный текст остаётся для клиентов вне NIKA — external_client_name).
  void (async () => {
    try {
      const res = await getCoachClients();
      const names = (res.clients ?? [])
        .map((c) => c.name)
        .filter((n): n is string => Boolean(n));
      if (names.length) {
        const dl = document.createElement("datalist");
        dl.id = "clients-dl";
        dl.innerHTML = names.map((n) => `<option value="${esc(n)}"></option>`).join("");
        view.appendChild(dl);
        view.querySelector<HTMLInputElement>(".js-ev-client")!
          .setAttribute("list", "clients-dl");
      }
    } catch {
      /* без списка — свободный ввод */
    }
  })();

  const body = view.querySelector<HTMLElement>(".js-schedule-body")!;
  const load = (period: "today" | "tomorrow" | "week"): void => {
    body.innerHTML = `<p class="muted">${t("cabinet.push_working")}</p>`;
    void (async () => {
      let data: ScheduleData;
      try {
        data = await getCoachSchedule(period);
      } catch {
        body.innerHTML = "";
        body.appendChild(failBlock());
        return;
      }
      if (data.ok !== true) {
        body.innerHTML = `<p class="muted">${esc(data.error ?? "")}</p>`;
        return;
      }
      renderScheduleList(body, data, () => load(period));
    })();
  };

  view.querySelectorAll<HTMLButtonElement>(".js-period").forEach((btn) => {
    btn.addEventListener("click", () =>
      load(btn.dataset.period as "today" | "tomorrow" | "week"));
  });

  // Новая тренировка — gated (kind=schedule_upsert).
  const status = view.querySelector<HTMLElement>(".js-ev-status")!;
  view.querySelector<HTMLButtonElement>(".js-ev-add")!
    .addEventListener("click", () => {
      const clientName =
        view.querySelector<HTMLInputElement>(".js-ev-client")!.value.trim();
      const startRaw =
        view.querySelector<HTMLInputElement>(".js-ev-start")!.value;
      const activity =
        view.querySelector<HTMLInputElement>(".js-ev-activity")!.value.trim();
      const location =
        view.querySelector<HTMLInputElement>(".js-ev-location")!.value.trim();
      if (!clientName || !startRaw) {
        status.textContent = t("error.generic");
        return;
      }
      // datetime-local → ISO со смещением Minsk (пилот: BY без DST).
      const startAt = `${startRaw}:00${MINSK_OFFSET}`;
      status.textContent = t("cabinet.saved");
      void (async () => {
        try {
          const res = await postCoachSchedule([
            {
              client_name: clientName,
              start_at: startAt,
              ...(activity ? { activity } : {}),
              ...(location ? { location } : {}),
            },
          ]);
          mountGatedResult(status, res, (ok) => {
            if (ok) load("today");
          });
        } catch (err) {
          status.textContent = errText(err);
        }
      })();
    });

  load("today");
}

function renderScheduleList(
  body: HTMLElement,
  data: ScheduleData,
  onChanged: () => void,
): void {
  const labels = statusLabels();
  if (!data.events.length) {
    body.innerHTML = `<p class="muted">${t("schedule.no_events")}</p>`;
    return;
  }
  body.innerHTML = `<ul class="list">${data.events
    .map(
      (e) =>
        `<li class="list__row list__row--stack" data-event="${e.event_id}">
           <span class="list__what">${esc(e.name ?? t("pulse.external"))}${
             e.activity ? ` <span class="muted">${esc(e.activity)}</span>` : ""
           }${
             e.location ? `<br><span class="muted">${esc(e.location)}</span>` : ""
           }</span>
           <span class="list__when">${fmtDateTime(e.start_at)}</span>
           <span class="list__when">${esc(labels[e.status] ?? e.status)}</span>
           <span class="row">
             <button class="linkbtn js-ev-done">${labels.completed}</button>
             <button class="linkbtn js-ev-cancel">${labels.cancelled}</button>
             <button class="linkbtn js-ev-shift">${t("schedule.shift_2h")}</button>
           </span>
         </li>`,
    )
    .join("")}</ul>
    <p class="muted" id="sched-status"></p>`;

  const statusEl = body.querySelector<HTMLElement>("#sched-status")!;
  const act = (
    row: HTMLElement,
    patch: { status?: string; start_at?: string },
  ): void => {
    void (async () => {
      try {
        const res = await patchCoachScheduleEvent(Number(row.dataset.event), patch);
        if (res.ok === true) {
          statusEl.textContent = t("schedule.updated");
          onChanged();
        } else {
          statusEl.textContent =
            typeof res.error === "string" ? res.error : t("error.generic");
        }
      } catch (err) {
        statusEl.textContent = errText(err);
      }
    })();
  };

  body.querySelectorAll<HTMLElement>("[data-event]").forEach((row) => {
    row.querySelector(".js-ev-done")!.addEventListener("click", () =>
      act(row, { status: "completed" }));
    row.querySelector(".js-ev-cancel")!.addEventListener("click", () =>
      act(row, { status: "cancelled" }));
    row.querySelector(".js-ev-shift")!.addEventListener("click", () => {
      const startIso = (
        data.events.find((e) => String(e.event_id) === row.dataset.event) as
          | ScheduleEvent
          | undefined
      )?.start_at;
      if (!startIso) return;
      const shifted = new Date(new Date(startIso).getTime() + 2 * 3600 * 1000);
      act(row, { start_at: shifted.toISOString() });
    });
  });
}

// ── БИЗНЕС (тренер, Phase 15 E1) ─────────────────────────────────────────────
// §7: реферальные ссылки/QR + кошелёк/сеть за флагами экономики.
// Display-only (§12: «не переписываем, только отображаем»): данные читает
// GET /coach/business, который сам скрывает секции по флагам (DoD-9) — клиент
// показывает только пришедшее. Ссылку строит СЕРВЕР (I-7); QR — из неё же.

function referralStatusLabels(): Record<string, string> {
  return {
    pending: t("business.status.pending"),
    converted: t("business.status.converted"),
    expired: t("business.status.expired"),
  };
}

export function renderBusinessScreen(container: HTMLElement): void {
  container.innerHTML = "";
  void (async () => {
    let data: CoachBusiness;
    try {
      data = await getCoachBusiness();
    } catch {
      container.appendChild(failBlock());
      return;
    }
    if (data.ok !== true) {
      container.appendChild(failBlock());
      return;
    }

    const link = data.referral_link ?? "";
    const refCard = link
      ? `<div class="card">
           <div class="kv"><span class="kv__k">${t("business.referral_code")}</span>
             <span class="kv__v">${esc(data.referral_code ?? "—")}</span></div>
           <div class="kv"><span class="kv__k">${t("business.referral_link")}</span>
             <span class="kv__v">${esc(link)}</span></div>
           <div class="row">
             <button class="linkbtn js-biz-copy">${t("business.copy")}</button>
           </div>
           <p class="muted js-biz-copy-status"></p>
           <div class="row">${qrSvg(link)}</div>
           <p class="muted">${t("business.qr_hint")}</p>
         </div>`
      : `<p class="muted">${t("business.no_link")}</p>`;

    const ref = data.referrals;
    const labels = referralStatusLabels();
    const rows = ref.referrals ?? [];
    const refList = rows.length
      ? `<ul class="list">${rows
          .map((r) => {
            const when = r.converted_at ?? r.referred_at;
            return `<li class="list__row list__row--stack">
               <span class="list__what">${esc(r.client_name ?? r.referral_code)}</span>
               <span class="list__when">${esc(labels[r.status] ?? r.status)}${
                 when ? ` · ${t("business.since", { date: fmtDate(when) })}` : ""
               }</span>
             </li>`;
          })
          .join("")}</ul>`
      : `<p class="muted">${t("business.referral_empty")}</p>`;
    const summary = ref.summary;
    const sumCard = summary
      ? `<div class="card">
           <div class="kv"><span class="kv__k">${t("business.summary_pending")}</span>
             <span class="kv__v">${fmtNumber(summary.pending)}</span></div>
           <div class="kv"><span class="kv__k">${t("business.summary_converted")}</span>
             <span class="kv__v">${fmtNumber(summary.converted)}</span></div>
           <div class="kv"><span class="kv__k">${t("business.summary_expired")}</span>
             <span class="kv__v">${fmtNumber(summary.expired)}</span></div>
         </div>`
      : "";

    // Кошелёк/сеть — ТОЛЬКО при включённой экономике/платежах (DoD-9);
    // сервер не прислал секцию → не рисуем. network error:"disabled" —
    // выключена сеть тренеров (COACH_TO_COACH_REFERRAL_ENABLED), не падаем.
    let walletHtml = "";
    const cash = data.cashback;
    const net = data.network;
    if (cash || net) {
      let body = "";
      if (cash?.ok && cash.totals) {
        body += `<h3 class="screen__subtitle">${t("business.cashback_title")}</h3>
           <div class="card">
             <div class="kv"><span class="kv__k">${t("business.pending")}</span>
               <span class="kv__v">${fmtNumber(cash.totals.cashback_pending)}</span></div>
             <div class="kv"><span class="kv__k">${t("business.confirmed")}</span>
               <span class="kv__v">${fmtNumber(cash.totals.cashback_confirmed)}</span></div>
             <div class="kv"><span class="kv__k">${t("business.network_pending")}</span>
               <span class="kv__v">${fmtNumber(cash.totals.network_pending)}</span></div>
             <div class="kv"><span class="kv__k">${t("business.network_confirmed")}</span>
               <span class="kv__v">${fmtNumber(cash.totals.network_confirmed)}</span></div>
             ${
               cash.boost_pct != null
                 ? `<div class="kv"><span class="kv__k">${t("business.boost")}</span>
               <span class="kv__v">${fmtNumber(cash.boost_pct)}%</span></div>`
                 : ""
             }
           </div>`;
      } else if (cash) {
        body += `<p class="muted">${t("business.no_cashback")}</p>`;
      }
      if (net?.ok) {
        body += `<h3 class="screen__subtitle">${t("business.network_title")}</h3>
           <div class="card">
             <div class="kv"><span class="kv__k">${t("business.network_total")}</span>
               <span class="kv__v">${fmtNumber(net.total_referred ?? 0)}</span></div>
             <div class="kv"><span class="kv__k">${t("business.network_qualified")}</span>
               <span class="kv__v">${fmtNumber(net.qualified_count ?? 0)}</span></div>
             ${
               net.rate_pct != null
                 ? `<div class="kv"><span class="kv__k">${t("business.rate")}</span>
               <span class="kv__v">${fmtNumber(net.rate_pct)}%</span></div>`
                 : ""
             }
           </div>`;
      } else if (net) {
        body += `<p class="muted">${
          net.error === "disabled"
            ? t("business.network_off")
            : t("business.no_network")
        }</p>`;
      }
      walletHtml = `<h3 class="screen__subtitle">${t("business.wallet_title")}</h3>${body}`;
    }

    const view = shell(
      "screen",
      `<h2 class="screen__title">${t("business.title")}</h2>
       <h3 class="screen__subtitle">${t("business.referral_title")}</h3>
       ${refCard}
       <h3 class="screen__subtitle">${t("business.referrals_title")}</h3>
       ${sumCard}
       ${refList}
       ${walletHtml}`,
    );
    container.appendChild(view);

    const copyStatus = view.querySelector<HTMLElement>(".js-biz-copy-status");
    view.querySelector<HTMLButtonElement>(".js-biz-copy")
      ?.addEventListener("click", () => {
        void navigator.clipboard
          .writeText(link)
          .then(() => {
            if (copyStatus) copyStatus.textContent = t("business.copied");
          })
          .catch(() => {
            if (copyStatus) copyStatus.textContent = t("error.generic");
          });
      });
  })();
}
